'use client';

import { PageHeader } from '@eliadmin/core/src/components/homepage/page-header';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import type { EliPageProps } from '@eliadmin/core/src/i18n/i18n-types';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { normalizeRetiredJobportalSort } from '@eliadmin/core/src/utils/retired-list-sort';
import { useSearchParams } from 'next/navigation';
import { Suspense, useState } from 'react';
import { CurriculumsFilterLeft } from '../../components/jobportal/curriculums/curriculums-filter-left';
import { JobportalCurriculumsHeader } from '../../components/jobportal/curriculums/jobportal-curriculums-header';
import { JobPortalCurriculumsContainer } from '../../components/jobportal/jobportal-curriculums-container';
import {
  JobportalCurriculumsFilterParamsSync,
  JobportalCurriculumsStoreProvider,
} from '../../stores/jobportal-curriculums-store';
import { getSlugFromPage } from '@eliadmin/core/src/utils/url-utils';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { FaLock } from 'react-icons/fa';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import Link from '@eliadmin/core/src/components/link';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { CurriculumAccessPaymentDialog } from '../../components/profile/curriculum-access-payment-dialog';
import { useQuery } from '@tanstack/react-query';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { fetchPaymentTypes } from '@eliadmin/core/src/api/jobportal/offers-api';

export default function CurriculumsPage(props: EliPageProps) {
  const searchParams = useSearchParams();
  const { t } = useTranslations();
  const { me, isInitialized } = useCustomerStore();
  const locale = useLocaleStore(s => s.currentLocale);
  const [isOpenCurriculumAccessDialog, setIsOpenCurriculumAccessDialog] = useState(false);

  const HAS_CURRICULUM_ACCESS = me?.customer?.active_subscriptions?.some(
    subscription => subscription.name === 'Prístup k životopisom',
  );

  // Fetch payment methods for the dialog
  const { data: payments } = useQuery({
    queryKey: ['cart-payments'],
    queryFn: () =>
      fetchPaymentTypes({ locale, authToken: me?.authToken || '' }).then((r: any) => r.data),
  });

  const search = searchParams.get('search') || undefined;
  const categories = searchParams.get('categories') || undefined;
  const employmentTypes = searchParams.get('employment_types') || undefined;
  const drivingLicenses = searchParams.get('driving_licenses') || undefined;
  const language = searchParams.get('language') || undefined;
  const { order, orderBy } = normalizeRetiredJobportalSort({
    order: searchParams.get('order') || undefined,
    orderBy: searchParams.get('orderBy') || undefined,
  });

  if (!isInitialized) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (!HAS_CURRICULUM_ACCESS) {
    return (
      <Container className="flex h-[400px] items-center justify-center">
        <div className="flex flex-col items-center justify-center text-center">
          <FaLock className="mx-auto mb-4 h-12 w-12 text-gray-500" />
          <h1 className="mb-4 text-2xl font-bold">{t('text3471')}</h1>
          <p className="mb-6">
            Aby ste mohli pristupovať k životopisom,
            <br /> je nutné si zakúpiť predplatné.
          </p>
          {me && me?.customer?.customer_type?.type !== 'company' && (
            <p className="mb-6">
              Pristupovať k životopisom môžu len firmy,
              <br /> ktoré majú aktívne predplatné.
            </p>
          )}
          {me?.customer?.customer_type?.type === 'company' ? (
            <Button onClick={() => setIsOpenCurriculumAccessDialog(true)} className="text-primary">
              {t('text3124')}
            </Button>
          ) : (
            <Button asChild>
              <Link href={'/cennik'} className="text-primary">
                Cenník služieb
              </Link>
            </Button>
          )}
        </div>
        <CurriculumAccessPaymentDialog
          open={isOpenCurriculumAccessDialog}
          setOpen={setIsOpenCurriculumAccessDialog}
          payments={payments}
        />
      </Container>
    );
  }

  return (
    <>
      <PageHeader
        breadcrumb={{
          name: t('text1739'),
          slug: getSlugFromPage('curriculum-vitaes'),
        }}
      />
      <JobportalCurriculumsStoreProvider
        state={{
          filter: {
            search,
            // min_salary: minSalary ? minSalary : undefined,
            // max_salary: maxSalary ? maxSalary : undefined,
            categories: categories?.length ? categories.split(',') : [],
            employment_types: employmentTypes?.length ? employmentTypes.split(',') : [],
            // salary_types: salaryTypes?.length ? salaryTypes.split(',') : [],
            // working_hours: workingHours?.length ? workingHours.split(',') : [],
            driving_licenses: drivingLicenses?.length ? drivingLicenses.split(',') : [],
            languages: language?.length ? language.split(',') : [],
            // city: city?.length ? city.split(',') : [],
            // distance: distance?.length ? distance.split(',') : [],
            order: (order as any) || 'desc',
            orderBy: (orderBy as any) || 'created_at',
          },
        }}
      >
        <JobportalCurriculumsFilterParamsSync />
        <Container
          className={cn('flex flex-col gap-8 py-8', 'ea-job-portal-curriculums__container')}
        >
          {/* <JobPortalOffersFilter /> */}
          <div className={cn('grid w-full grid-cols-12 gap-8', 'ea-job-portal-curriculums__grid')}>
            <div
              className={cn(
                'col-span-12 md:col-span-5 lg:col-span-3',
                'ea-job-portal-curriculums__filter',
              )}
            >
              <div className="hidden md:block">
                <CurriculumsFilterLeft />
              </div>
            </div>
            <div
              className={cn('col-span-12 md:col-span-7 lg:col-span-9', 'ea-job-portal-curriculums')}
            >
              <JobportalCurriculumsHeader />
              <Suspense fallback={<div />}>
                <JobPortalCurriculumsContainer />
              </Suspense>
            </div>
          </div>
        </Container>
      </JobportalCurriculumsStoreProvider>
    </>
  );
}
